// src/components/MobilityTable.tsx
import { useQuery } from "@tanstack/react-query";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Skeleton,
} from "@mui/material";
import { MobilityRecord } from "../../interface/employment";
import { apiGetMedicMobility } from "../../api/employment";
import { contentSty, headerSty } from "../table-styles";
import { useTheme } from "@mui/material/styles";

interface Props {
  medicId: string;
}

const MobilityTable: React.FC<Props> = ({ medicId }) => {
  const theme = useTheme();

  const { data, isLoading, isError } = useQuery<MobilityRecord[]>({
    queryKey: ["medicMobility", medicId],
    queryFn: () => apiGetMedicMobility(medicId),
    enabled: !!medicId, // Prevent early call
  });
  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };

  if (!data || data.length === 0)
    return (
      <Typography sx={{ textAlign: "center" }}>
        No mobility data found.
      </Typography>
    );

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headStyles }}>Year</TableCell>
            <TableCell sx={{ ...headStyles }}>Quarter</TableCell>
            <TableCell sx={{ ...headStyles }}>Leave</TableCell>
            <TableCell sx={{ ...headStyles }}>Overtime</TableCell>
            <TableCell sx={{ ...headStyles }}>Full-Time Months</TableCell>
            <TableCell sx={{ ...headStyles }}>Locum Months</TableCell>
            <TableCell sx={{ ...headStyles }}>Mobility Score</TableCell>
            <TableCell sx={{ ...headStyles }}>Profile Completion</TableCell>
            <TableCell sx={{ ...headStyles }}>Role Changes</TableCell>
            <TableCell sx={{ ...headStyles }}>Location Changes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((record) => (
            <TableRow key={record._id}>
              <TableCell>{record.year}</TableCell>
              <TableCell>{record.quarter}</TableCell>
              <TableCell>{record.countLeave}</TableCell>
              <TableCell>{record.countOvertime.toFixed(1)}</TableCell>
              <TableCell>{record.fullTimeMonths}</TableCell>
              <TableCell>{record.locumMonths}</TableCell>
              <TableCell>{record.mobilityScore}</TableCell>
              <TableCell>{record.profileCompletion}%</TableCell>
              <TableCell>{record.roleChanges}</TableCell>
              <TableCell>{record.locationChanges}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isLoading && (
        <Box sx={{ width: "100%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={50}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={50}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={50}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
};

export default MobilityTable;
